import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Box
} from '@mui/material';
import MessageTemplateService from '../../../services/messageTemplateService';
import { EmailConfig, SMSConfig, VoiceConfig, ChatConfig } from '../channels';
import VariableSelector from '../VariableSelector';

const BlastMessageConfig = ({
  template,
  content,
  useRelativeSchedule,
  onTemplateChange,
  onContentChange,
  onUseRelativeScheduleChange,
  templates,
  channel,
  channelConfig,
  onChannelConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onChannelChange
}) => {
  const [variableCategories, setVariableCategories] = useState([]);
  const [showVariables, setShowVariables] = useState(false);

  useEffect(() => {
    const fetchAvailableVariables = async () => {
      try {
        const categories = await MessageTemplateService.getAvailableVariables();
        setVariableCategories(categories);
      } catch (err) {
        console.error('Failed to fetch available variables:', err);
      }
    };
    fetchAvailableVariables();
  }, []);

  const insertVariable = (categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    onContentChange(content + variable);
  };

  const renderChannelConfig = () => {
    const commonProps = {
      config: channelConfig,
      onConfigChange: onChannelConfigChange,
      communicationEndpoints,
      onEndpointChange
    };

    switch (channel) {
      case 'email':
        return <EmailConfig {...commonProps} />;
      case 'sms':
        return <SMSConfig {...commonProps} />;
      case 'voice':
        return <VoiceConfig {...commonProps} />;
      case 'chat':
        return <ChatConfig {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select
              value={channel}
              onChange={(e) => onChannelChange(e.target.value)}
              label="Channel"
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="voice">Voice</MenuItem>
              <MenuItem value="chat">Chat</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {channel !== 'voice' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={template}
                onChange={(e) => onTemplateChange(e.target.value)}
                label="Template"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {!template && channel !== 'voice' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message Content"
              value={content}
              onChange={(e) => onContentChange(e.target.value)}
              error={!template && !content}
              helperText={!template && !content ? "Either template or content is required" : ""}
            />
            <VariableSelector
              variableCategories={variableCategories}
              onInsertVariable={insertVariable}
              showVariables={showVariables}
              onToggleVariables={() => setShowVariables(!showVariables)}
            />
          </Grid>
        )}
        {channel === 'voice' && (
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'background.paper', 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 1, 
              mb: 2,
              boxShadow: 1
            }}>
              <Typography variant="body2" color="text.primary">
                Voice message content and configuration are managed through the voice platform settings below. 
                Each platform (Bland AI, VAPI, ElevenLabs, Twilio) has its own message configuration interface.
              </Typography>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Channel Configuration
          </Typography>
          <Paper sx={{ p: 2, mb: 3 }}>
            {renderChannelConfig()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BlastMessageConfig; 